import { useCallback, useMemo, useSyncExternalStore } from "react";

/** No `matchMedia` on the server or in jsdom: treat every query as not matching. */
const getMediaQueryList = (query: string): MediaQueryList | null =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query)
    : null;

const noMatchOnServer = () => false;

/**
 * Whether a CSS media query matches, kept in sync as it changes (viewport
 * resized past a breakpoint, motion preference toggled in the OS).
 *
 * `useSyncExternalStore` reads the query during the first render rather than
 * in an effect, so a component gated on it never renders the wrong branch
 * first. AuthShell relies on that: under 1100px the lazy carousel is never
 * rendered, so phones never download it.
 *
 * Pass the bare query, as in `authMedia` — e.g. `"(max-width: 68.75rem)"`.
 */
export const useMediaQuery = (query: string): boolean => {
  const mediaQueryList = useMemo(() => getMediaQueryList(query), [query]);

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!mediaQueryList) return () => undefined;
      mediaQueryList.addEventListener("change", onChange);
      return () => mediaQueryList.removeEventListener("change", onChange);
    },
    [mediaQueryList],
  );

  const getSnapshot = useCallback(() => mediaQueryList?.matches ?? false, [mediaQueryList]);

  return useSyncExternalStore(subscribe, getSnapshot, noMatchOnServer);
};
